import * as React from 'react';
import { useForm, useFormState } from 'react-final-form';
import {
    ArrayInput,
    Button,
    DateField,
    Edit,
    EditButton,
    FormDataConsumer,
    FormTab,
    FunctionField,
    List,
    NumberField,
    NumberInput,
    SaveButton,
    SelectInput,
    SimpleFormIterator,
    TabbedForm,
    TabbedFormTabs,
    TextField,
    TextInput,
    Toolbar,
    useInput,
    useNotify,
    useRefresh,
    useRecordContext
} from 'react-admin';
import {
    Button as MuiButton,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    TextField as MuiTextField,
} from '@material-ui/core';
import AdminPagination from '../AdminPagination';
import AdminSelectableDatagrid from '../AdminSelectableDatagrid';
import HumanRecordTitle from '../HumanRecordTitle';
import HumanDeleteButton from '../HumanDeleteButton';
import apiURL from '../apiURL';
import FrenchDateTimeInput from '../FrenchDateTimeInput';
import { ResourceListTitle } from '../AdminResourceTotalCount';

const EquipmentReturnMirror = () => {
    const form = useForm();
    const { values } = useFormState({
        subscription: { values: true },
    });

    const departure = Array.isArray(values?.equipment_departure)
        ? values.equipment_departure
        : [];

    const equipmentReturn = Array.isArray(values?.equipment_return)
        ? values.equipment_return
        : [];

    React.useEffect(() => {
        const nextReturn = departure.map((item: any, index: number) => {
            const sameName = equipmentReturn.find(
                (returnItem: any) => returnItem?.name === item?.name,
            );

            const previous = sameName || equipmentReturn[index] || {};

            return {
                name: item?.name || '',
                good_condition: !!previous.good_condition,
                anomaly: !!previous.anomaly,
            };
        });

        if (
            JSON.stringify(nextReturn) !==
            JSON.stringify(equipmentReturn)
        ) {
            form.change('equipment_return', nextReturn);
        }
    }, [departure, equipmentReturn, form]);

    return null;
};

const EquipmentCheckboxInput = ({
    source,
    oppositeSource,
    label,
}: {
    source: string;
    oppositeSource: string;
    label: string;
}) => {
    const { input } = useInput({ source });
    const { input: oppositeInput } = useInput({ source: oppositeSource });

    return (
        <FormControlLabel
            style={{ marginRight: 24, whiteSpace: 'nowrap' }}
            control={
                <Checkbox
                    name={input.name}
                    checked={!!input.value}
                    onChange={(event) => {
                        const checked = event.target.checked;
                        input.onChange(checked);

                        if (checked) {
                            oppositeInput.onChange(false);
                        }
                    }}
                    color="primary"
                />
            }
            label={label}
        />
    );
};

const BoxCustomerHandover = () => {
    const record = useRecordContext();
    const notify = useNotify();
    const refresh = useRefresh();

    const [loading, setLoading] = React.useState(false);
    const [confirmType, setConfirmType] = React.useState<string | null>(null);

    const [dialogStep, setDialogStep] =
        React.useState<'choice' | 'incident' | 'saved'>('choice');

    const [incidentMaterial, setIncidentMaterial] =
        React.useState('');

    const [incidentProblem, setIncidentProblem] =
        React.useState('');

    const [incidentAction, setIncidentAction] =
        React.useState('');

    const [incidentResult, setIncidentResult] =
        React.useState('');

    if (!record) return null;

    const alreadySent = !!record.customer_handover_email_sent_at;

    const typeLabel =
        record.customer_handover_type === 'delivered_installed_tested'
            ? 'Livrée, installée et testée'
            : record.customer_handover_type === 'delivered_tested'
            ? 'Livrée et testée'
            : '';

    const formatDate = (value) => {
        if (!value) return '';

        const date = new Date(value);

        if (Number.isNaN(date.getTime())) {
            return '';
        }

        return date.toLocaleString('fr-FR');
    };

    const confirmSend = async () => {
        if (!confirmType || loading || alreadySent) return;

        const selectedType = confirmType;

        setConfirmType(null);
        setLoading(true);

        try {
            const token = localStorage.getItem('auth') || '';

            const response = await fetch(
                `${apiURL}/boxreservation/${record.id}/customer-handover`,
                {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        type: selectedType,
                    }),
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            notify(
                'Remise enregistrée. Email de confirmation envoyé au client.',
                { type: 'success' }
            );

            refresh();
        } catch (error) {
            console.error(error);

            notify(
                "La confirmation de remise n'a pas pu être envoyée.",
                { type: 'error' }
            );
        } finally {
            setLoading(false);
        }
    };

    const saveIncident = async () => {
        if (
            !confirmType ||
            !incidentMaterial.trim() ||
            !incidentProblem.trim() ||
            loading
        ) {
            notify(
                'Renseigne le matériel concerné et le problème constaté.',
                { type: 'warning' }
            );
            return;
        }

        setLoading(true);

        try {
            const token =
                localStorage.getItem('auth') || '';

            const response = await fetch(
                `${apiURL}/boxreservation/${record.id}/customer-handover/incident`,
                {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        type: confirmType,
                        material: incidentMaterial.trim(),
                        problem: incidentProblem.trim(),
                        action: incidentAction.trim(),
                        result: incidentResult.trim(),
                    }),
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            notify(
                'Incident enregistré. Aucun email de conformité envoyé.',
                { type: 'success' }
            );

            setDialogStep('saved');
        } catch (error) {
            console.error(error);

            notify(
                "L'incident n'a pas pu être enregistré.",
                { type: 'error' }
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            style={{
                marginTop: 28,
                marginBottom: 28,
                padding: 20,
                border: '1px solid #dddddd',
                borderRadius: 6,
                background: '#fafafa',
            }}
        >
            <div
                style={{
                    fontWeight: 700,
                    marginBottom: 14,
                }}
            >
                REMISE AU CLIENT
            </div>

            {alreadySent ? (
                <div style={{ lineHeight: 1.7 }}>
                    <div
                        style={{
                            fontWeight: 700,
                            color: '#2e7d32',
                        }}
                    >
                        ✓ {typeLabel}
                    </div>

                    {record.customer_handover_at && (
                        <div>
                            Remise enregistrée le :{' '}
                            {formatDate(record.customer_handover_at)}
                        </div>
                    )}

                    <div>
                        Email envoyé le :{' '}
                        {formatDate(record.customer_handover_email_sent_at)}
                    </div>

                    {record.customer_handover_incident_at && (
                        <div
                            style={{
                                marginTop: 18,
                                paddingTop: 14,
                                borderTop: '1px solid #dddddd',
                            }}
                        >
                            <div
                                style={{
                                    fontWeight: 700,
                                    color: '#ed6c02',
                                    marginBottom: 8,
                                }}
                            >
                                ⚠ INCIDENT CONSTATÉ À LA REMISE
                            </div>

                            <div>
                                <strong>Matériel :</strong>{' '}
                                {record.customer_handover_incident_material || '—'}
                            </div>

                            <div>
                                <strong>Problème :</strong>{' '}
                                {record.customer_handover_incident_problem || '—'}
                            </div>

                            <div>
                                <strong>Action effectuée :</strong>{' '}
                                {record.customer_handover_incident_action || '—'}
                            </div>

                            <div>
                                <strong>Résultat :</strong>{' '}
                                {record.customer_handover_incident_result || '—'}
                            </div>

                            {record.customer_handover_incident_resolved_at && (
                                <div
                                    style={{
                                        marginTop: 8,
                                        fontWeight: 700,
                                        color: '#2e7d32',
                                    }}
                                >
                                    ✓ Incident résolu lors de la remise
                                </div>
                            )}
                        </div>
                    )}
                </div>
            ) : (
                <>
                    <div
                        style={{
                            marginBottom: 14,
                            color: '#555555',
                        }}
                    >
                        Choisis uniquement l'action réellement effectuée
                        sur place avec le client.
                    </div>

                    <div
                        style={{
                            display: 'flex',
                            gap: 12,
                            flexWrap: 'nowrap',
                            width: '100%',
                        }}
                    >
                        <MuiButton
                            variant="contained"
                            color="primary"
                            disabled={loading}
                            onClick={() =>
                                setConfirmType('delivered_tested')
                            }
                            style={{
                                flex: 1,
                                minHeight: 64,
                                textAlign: 'center',
                                lineHeight: 1.25,
                                whiteSpace: 'normal',
                            }}
                        >
                            LIVRÉE ET TESTÉE
                        </MuiButton>

                        <MuiButton
                            variant="contained"
                            color="primary"
                            disabled={loading}
                            onClick={() =>
                                setConfirmType(
                                    'delivered_installed_tested'
                                )
                            }
                            style={{
                                flex: 1,
                                minHeight: 64,
                                textAlign: 'center',
                                lineHeight: 1.25,
                                whiteSpace: 'normal',
                            }}
                        >
                            LIVRÉE, INSTALLÉE ET TESTÉE
                        </MuiButton>
                    </div>
                </>
            )}

            <Dialog
                open={!!confirmType}
                onClose={() => {
                    if (!loading) {
                        setConfirmType(null);
                        setDialogStep('choice');
                    }
                }}
                fullWidth
                maxWidth="sm"
            >
                {dialogStep === 'choice' && (
                    <>
                        <DialogTitle>
                            Confirmer la remise
                        </DialogTitle>

                        <DialogContent>
                            <DialogContentText>
                                {confirmType === 'delivered_installed_tested'
                                    ? 'La Box a-t-elle bien été livrée, installée et testée sur place avec le client ?'
                                    : 'La Box a-t-elle bien été livrée et testée sur place avec le client ?'}
                            </DialogContentText>
                        </DialogContent>

                        <DialogActions
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'stretch',
                                gap: 8,
                                padding: 16,
                            }}
                        >
                            <MuiButton
                                variant="contained"
                                color="primary"
                                disabled={loading}
                                onClick={confirmSend}
                            >
                                TOUT EST OK
                            </MuiButton>

                            <MuiButton
                                variant="outlined"
                                color="secondary"
                                disabled={loading}
                                onClick={() =>
                                    setDialogStep('incident')
                                }
                            >
                                SIGNALER UN PROBLÈME
                            </MuiButton>

                            <MuiButton
                                disabled={loading}
                                onClick={() => {
                                    setConfirmType(null);
                                    setDialogStep('choice');
                                }}
                            >
                                ANNULER
                            </MuiButton>
                        </DialogActions>
                    </>
                )}

                {dialogStep === 'incident' && (
                    <>
                        <DialogTitle>
                            Signaler un problème
                        </DialogTitle>

                        <DialogContent>
                            <TextInput
                                source="handover_incident_material_ui"
                                label="Matériel concerné"
                                fullWidth
                                value={incidentMaterial}
                                onChange={(event) =>
                                    setIncidentMaterial(
                                        event.target.value
                                    )
                                }
                            />

                            <TextInput
                                source="handover_incident_problem_ui"
                                label="Problème constaté"
                                multiline
                                rows={3}
                                fullWidth
                                value={incidentProblem}
                                onChange={(event) =>
                                    setIncidentProblem(
                                        event.target.value
                                    )
                                }
                            />

                            <TextInput
                                source="handover_incident_action_ui"
                                label="Action effectuée"
                                multiline
                                rows={3}
                                fullWidth
                                value={incidentAction}
                                onChange={(event) =>
                                    setIncidentAction(
                                        event.target.value
                                    )
                                }
                            />

                            <TextInput
                                source="handover_incident_result_ui"
                                label="Résultat"
                                multiline
                                rows={3}
                                fullWidth
                                value={incidentResult}
                                onChange={(event) =>
                                    setIncidentResult(
                                        event.target.value
                                    )
                                }
                            />
                        </DialogContent>

                        <DialogActions>
                            <MuiButton
                                disabled={loading}
                                onClick={() =>
                                    setDialogStep('choice')
                                }
                            >
                                RETOUR
                            </MuiButton>

                            <MuiButton
                                variant="contained"
                                color="primary"
                                disabled={loading}
                                onClick={saveIncident}
                            >
                                ENREGISTRER L'INCIDENT
                            </MuiButton>
                        </DialogActions>
                    </>
                )}
                {dialogStep === 'saved' && (
                    <>
                        <DialogTitle>
                            Incident enregistré
                        </DialogTitle>

                        <DialogContent>
                            <DialogContentText>
                                L'incident est enregistré dans le dossier.
                                Aucun email de conformité n'a été envoyé.
                            </DialogContentText>
                        </DialogContent>

                        <DialogActions
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'stretch',
                                gap: 8,
                                padding: 16,
                            }}
                        >
                            <MuiButton
                                variant="contained"
                                color="primary"
                                disabled={loading}
                                onClick={confirmSend}
                            >
                                PROBLÈME RÉSOLU — CONFIRMER LA REMISE
                            </MuiButton>

                            <MuiButton
                                disabled={loading}
                                onClick={() => {
                                    setConfirmType(null);
                                    setDialogStep('choice');
                                }}
                            >
                                FERMER
                            </MuiButton>
                        </DialogActions>
                    </>
                )}

            </Dialog>
        </div>
    );
};


const STATUS_CHOICES = [
    { id: 'pending', name: 'En attente' },
    { id: 'available', name: 'Disponible' },
    { id: 'unavailable', name: 'Indisponible' },
    { id: 'proposed', name: 'Date proposée' },
    { id: 'customer_accepted', name: 'Accepté client' },
    { id: 'customer_declined', name: 'Refusé client' },
    { id: 'payment_sent', name: 'Paiement envoyé' },
    { id: 'paid', name: 'Payé' },
    { id: 'cancelled', name: 'Annulé' },
];


const BoxRefundPanel = () => {
    const record = useRecordContext();
    const notify = useNotify();
    const refresh = useRefresh();

    const [loading, setLoading] = React.useState(false);
    const [summary, setSummary] = React.useState<any>(null);
    const [mode, setMode] = React.useState<'total' | 'items' | 'custom'>('items');
    const [selectedKeys, setSelectedKeys] = React.useState<string[]>([]);
    const [customAmount, setCustomAmount] = React.useState('');
    const [reason, setReason] = React.useState('');
    const [confirmOpen, setConfirmOpen] = React.useState(false);

    const formatPrice = (value: any) =>
        new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'EUR',
        }).format(Number(value || 0));

    const formatDate = (value: any) => {
        if (!value) return '—';

        const date = new Date(value);

        if (Number.isNaN(date.getTime())) return '—';

        return date.toLocaleString('fr-FR');
    };

    const loadSummary = React.useCallback(async () => {
        if (!record?.id) return;

        setLoading(true);

        try {
            const token = localStorage.getItem('auth') || '';

            const response = await fetch(
                `${apiURL}/boxreservation/${record.id}/refund-summary`,
                {
                    method: 'GET',
                    headers: {
                        Accept: 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const data = await response.json();

            setSummary(data);
        } catch (error) {
            console.error(error);

            notify(
                "Le résumé des remboursements n'a pas pu être chargé.",
                { type: 'error' }
            );
        } finally {
            setLoading(false);
        }
    }, [record?.id, notify]);

    React.useEffect(() => {
        loadSummary();
    }, [loadSummary]);

    if (!record) return null;

    if (loading && !summary) {
        return <div>Chargement des informations Stripe…</div>;
    }

    if (!summary?.available) {
        return (
            <div
                style={{
                    padding: 16,
                    border: '1px solid #ddd',
                    borderRadius: 6,
                    background: '#fafafa',
                }}
            >
                Aucun paiement Stripe remboursable pour cette réservation.
            </div>
        );
    }

    const remaining = Number(summary.refundable_balance || 0);

    const selectedComponents = Array.isArray(summary.components)
        ? summary.components.filter((item) =>
              selectedKeys.includes(item.key)
          )
        : [];

    const selectedAmount = selectedComponents.reduce(
        (sum, item) => sum + Number(item.amount || 0),
        0
    );

    const requestedAmount =
        mode === 'total'
            ? remaining
            : mode === 'items'
            ? selectedAmount
            : Number(String(customAmount).replace(',', '.')) || 0;

    const toggleComponent = (key: string) => {
        setSelectedKeys((current) =>
            current.includes(key)
                ? current.filter((item) => item !== key)
                : [...current, key]
        );
    };

    const openConfirmation = () => {
        if (remaining <= 0) {
            notify('Cette réservation est déjà entièrement remboursée.', {
                type: 'warning',
            });
            return;
        }

        if (mode === 'items' && selectedKeys.length === 0) {
            notify('Sélectionne au moins un élément à rembourser.', {
                type: 'warning',
            });
            return;
        }

        if (
            mode === 'custom' &&
            (!Number.isFinite(requestedAmount) ||
                requestedAmount <= 0 ||
                requestedAmount > remaining)
        ) {
            notify(
                `Le montant doit être compris entre 0,01 € et ${formatPrice(
                    remaining
                )}.`,
                { type: 'warning' }
            );
            return;
        }

        if (requestedAmount <= 0 || requestedAmount > remaining) {
            notify('Montant de remboursement invalide.', {
                type: 'warning',
            });
            return;
        }

        setConfirmOpen(true);
    };

    const executeRefund = async () => {
        if (loading) return;

        setConfirmOpen(false);
        setLoading(true);

        const requestId =
            `admin-${record.id}-${Date.now()}-${Math.random()
                .toString(36)
                .slice(2, 10)}`;

        try {
            const token = localStorage.getItem('auth') || '';

            const response = await fetch(
                `${apiURL}/boxreservation/${record.id}/refund`,
                {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        request_id: requestId,
                        mode,
                        component_keys:
                            mode === 'items' ? selectedKeys : [],
                        amount:
                            mode === 'custom'
                                ? requestedAmount
                                : undefined,
                        reason: reason.trim(),
                    }),
                }
            );

            const data = await response.json().catch(() => null);

            if (!response.ok) {
                throw new Error(
                    data?.message || `HTTP ${response.status}`
                );
            }

            setSummary(data);
            setSelectedKeys([]);
            setCustomAmount('');
            setReason('');

            notify(
                `Remboursement de ${formatPrice(
                    requestedAmount
                )} envoyé à Stripe.`,
                { type: 'success' }
            );

            refresh();
        } catch (error) {
            console.error(error);

            notify(
                "Le remboursement n'a pas pu être effectué.",
                { type: 'error' }
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ width: '100%', maxWidth: 760 }}>
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns:
                        'repeat(auto-fit, minmax(180px, 1fr))',
                    gap: 12,
                    marginBottom: 24,
                }}
            >
                <div
                    style={{
                        padding: 14,
                        border: '1px solid #ddd',
                        borderRadius: 6,
                    }}
                >
                    <div style={{ fontSize: 12, color: '#666' }}>
                        TOTAL PAYÉ
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700 }}>
                        {formatPrice(summary.total_paid)}
                    </div>
                </div>

                <div
                    style={{
                        padding: 14,
                        border: '1px solid #ddd',
                        borderRadius: 6,
                    }}
                >
                    <div style={{ fontSize: 12, color: '#666' }}>
                        DÉJÀ REMBOURSÉ
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700 }}>
                        {formatPrice(summary.already_refunded)}
                    </div>
                </div>

                <div
                    style={{
                        padding: 14,
                        border: '1px solid #ddd',
                        borderRadius: 6,
                    }}
                >
                    <div style={{ fontSize: 12, color: '#666' }}>
                        RESTE REMBOURSABLE
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700 }}>
                        {formatPrice(summary.refundable_balance)}
                    </div>
                </div>
            </div>

            {remaining > 0 ? (
                <>
                    <div
                        style={{
                            fontWeight: 700,
                            marginBottom: 10,
                        }}
                    >
                        Type de remboursement
                    </div>

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={mode === 'total'}
                                onChange={() => setMode('total')}
                                color="primary"
                            />
                        }
                        label="Rembourser tout le solde restant"
                    />

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={mode === 'items'}
                                onChange={() => setMode('items')}
                                color="primary"
                            />
                        }
                        label="Rembourser des éléments précis"
                    />

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={mode === 'custom'}
                                onChange={() => setMode('custom')}
                                color="primary"
                            />
                        }
                        label="Rembourser un montant libre"
                    />

                    {mode === 'items' && (
                        <div
                            style={{
                                marginTop: 16,
                                marginBottom: 18,
                                padding: 14,
                                border: '1px solid #ddd',
                                borderRadius: 6,
                            }}
                        >
                            {(summary.components || []).map((item) => (
                                <div
                                    key={item.key}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 12,
                                        padding: '8px 0',
                                        borderBottom:
                                            '1px solid #eee',
                                    }}
                                >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={
                                                    item.refunded ||
                                                    selectedKeys.includes(
                                                        item.key
                                                    )
                                                }
                                                disabled={
                                                    item.refunded ||
                                                    loading
                                                }
                                                onChange={() =>
                                                    toggleComponent(
                                                        item.key
                                                    )
                                                }
                                                color="primary"
                                            />
                                        }
                                        label={
                                            item.refunded
                                                ? `${item.label} — Remboursé`
                                                : item.label
                                        }
                                    />

                                    <strong>
                                        {formatPrice(item.amount)}
                                    </strong>
                                </div>
                            ))}

                            <div
                                style={{
                                    marginTop: 14,
                                    fontWeight: 700,
                                }}
                            >
                                Montant sélectionné :{' '}
                                {formatPrice(selectedAmount)}
                            </div>
                        </div>
                    )}

                    {mode === 'custom' && (
                        <div style={{ marginTop: 16, marginBottom: 18 }}>
                            <MuiTextField
                                label="Montant à rembourser (€)"
                                type="number"
                                variant="outlined"
                                size="small"
                                value={customAmount}
                                onChange={(event) =>
                                    setCustomAmount(event.target.value)
                                }
                                inputProps={{
                                    min: 0.01,
                                    max: remaining,
                                    step: 0.01,
                                }}
                            />
                        </div>
                    )}

                    <div style={{ marginTop: 16, marginBottom: 18 }}>
                        <MuiTextField
                            label="Motif / note interne"
                            variant="outlined"
                            size="small"
                            fullWidth
                            multiline
                            rows={3}
                            value={reason}
                            onChange={(event) =>
                                setReason(event.target.value)
                            }
                        />
                    </div>

                    <div
                        style={{
                            marginBottom: 18,
                            fontWeight: 700,
                        }}
                    >
                        Montant du remboursement :{' '}
                        {formatPrice(requestedAmount)}
                    </div>

                    <MuiButton
                        variant="contained"
                        color="primary"
                        disabled={loading || requestedAmount <= 0}
                        onClick={openConfirmation}
                    >
                        {loading
                            ? 'Traitement…'
                            : 'REMBOURSER VIA STRIPE'}
                    </MuiButton>
                </>
            ) : (
                <div
                    style={{
                        marginBottom: 24,
                        padding: 14,
                        border: '1px solid #ddd',
                        borderRadius: 6,
                        fontWeight: 700,
                    }}
                >
                    Cette réservation est entièrement remboursée.
                </div>
            )}

            <div
                style={{
                    marginTop: 32,
                    fontWeight: 700,
                    marginBottom: 12,
                }}
            >
                HISTORIQUE DES REMBOURSEMENTS
            </div>

            {Array.isArray(summary.history) &&
            summary.history.length > 0 ? (
                summary.history.map((item) => (
                    <div
                        key={item.request_id}
                        style={{
                            padding: 14,
                            marginBottom: 10,
                            border: '1px solid #ddd',
                            borderRadius: 6,
                            background: '#fafafa',
                        }}
                    >
                        <div>
                            <strong>Date :</strong>{' '}
                            {formatDate(item.created_at)}
                        </div>

                        <div>
                            <strong>Montant :</strong>{' '}
                            {formatPrice(item.amount)}
                        </div>

                        <div>
                            <strong>Type :</strong>{' '}
                            {item.mode === 'total'
                                ? 'Total'
                                : item.mode === 'items'
                                ? 'Éléments'
                                : 'Montant libre'}
                        </div>

                        {Array.isArray(item.component_labels) &&
                            item.component_labels.length > 0 && (
                                <div>
                                    <strong>Éléments :</strong>{' '}
                                    {item.component_labels.join(' • ')}
                                </div>
                            )}

                        {item.reason && (
                            <div>
                                <strong>Motif :</strong>{' '}
                                {item.reason}
                            </div>
                        )}

                        <div>
                            <strong>Stripe :</strong>{' '}
                            {item.stripe_refund_id || '—'}
                        </div>

                        <div>
                            <strong>Statut :</strong>{' '}
                            {item.stripe_status || '—'}
                        </div>
                    </div>
                ))
            ) : (
                <div>Aucun remboursement enregistré.</div>
            )}

            <Dialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
            >
                <DialogTitle>
                    Confirmer le remboursement Stripe
                </DialogTitle>

                <DialogContent>
                    <DialogContentText>
                        Tu vas rembourser{' '}
                        <strong>
                            {formatPrice(requestedAmount)}
                        </strong>{' '}
                        sur le moyen de paiement utilisé par le client.
                        Cette action déclenche réellement le remboursement
                        chez Stripe.
                    </DialogContentText>

                    {mode === 'items' &&
                        selectedComponents.length > 0 && (
                            <div style={{ marginTop: 12 }}>
                                {selectedComponents.map((item) => (
                                    <div key={item.key}>
                                        • {item.label} —{' '}
                                        {formatPrice(item.amount)}
                                    </div>
                                ))}
                            </div>
                        )}
                </DialogContent>

                <DialogActions>
                    <MuiButton
                        onClick={() => setConfirmOpen(false)}
                        disabled={loading}
                    >
                        ANNULER
                    </MuiButton>

                    <MuiButton
                        onClick={executeRefund}
                        color="primary"
                        variant="contained"
                        disabled={loading}
                    >
                        CONFIRMER LE REMBOURSEMENT
                    </MuiButton>
                </DialogActions>
            </Dialog>
        </div>
    );
};

const MANUAL_STATUS_CHOICES = [
    { id: 'pending', name: 'En attente' },
    { id: 'available', name: 'Disponible' },
    { id: 'unavailable', name: 'Indisponible' },
    { id: 'cancelled', name: 'Annulé' },
];

const BoxProposeDateButton = ({
    proposedDate,
}: {
    proposedDate?: string;
}) => {
    const record = useRecordContext();
    const notify = useNotify();
    const refresh = useRefresh();
    const [loading, setLoading] = React.useState(false);
    const [confirmOpen, setConfirmOpen] = React.useState(false);

    if (!record) return null;

    const proposeDate = async () => {
        if (!proposedDate) {
            notify(
                'Renseigne une nouvelle date proposée avant de continuer.',
                { type: 'warning' }
            );
            return;
        }

        setConfirmOpen(true);
        return;
    };

    const confirmProposeDate = async () => {
        if (!proposedDate || loading) return;

        setConfirmOpen(false);
        setLoading(true);

        try {
            const token = localStorage.getItem('auth') || '';

            const response = await fetch(
                `${apiURL}/boxreservation/${record.id}/proposed-date`,
                {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        proposed_date: proposedDate,
                    }),
                }
            );

            if (!response.ok) {
                throw new Error(
                    `HTTP ${response.status}`
                );
            }

            notify(
                'Nouvelle date proposée au client. Email envoyé.',
                { type: 'success' }
            );

            refresh();
        } catch (error) {
            console.error(error);

            notify(
                "La proposition de nouvelle date n'a pas pu être envoyée.",
                { type: 'error' }
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
        <MuiButton
            color="primary"
            size="small"
            onClick={proposeDate}
            disabled={loading || !proposedDate}
        >
            {loading
                ? 'Envoi en cours...'
                : 'Indisponible — proposer cette date'}
        </MuiButton>

        <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
            <DialogTitle>Confirmer la nouvelle date</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Envoyer cette nouvelle date au client ?
                </DialogContentText>
                <strong>
                    {proposedDate
                        ? new Date(proposedDate).toLocaleString('fr-FR')
                        : ''}
                </strong>
            </DialogContent>
            <DialogActions>
                <MuiButton onClick={() => setConfirmOpen(false)}>
                    Annuler
                </MuiButton>
                <MuiButton color="primary" onClick={confirmProposeDate}>
                    Envoyer
                </MuiButton>
            </DialogActions>
        </Dialog>
        </>
    );
};



const getBoxReservationTimezone = (record) => {
    const stored = String(record?.event_timezone || '').trim();

    if (stored) return stored;

    const code = String(record?.delivery_zone_code || '').trim();

    const timezones = {
        '75': 'Europe/Paris',
        '77': 'Europe/Paris',
        '78': 'Europe/Paris',
        '91': 'Europe/Paris',
        '92': 'Europe/Paris',
        '93': 'Europe/Paris',
        '94': 'Europe/Paris',
        '95': 'Europe/Paris',
        '971': 'America/Guadeloupe',
        '972': 'America/Martinique',
        '973': 'America/Cayenne',
        '974': 'Indian/Reunion',
        '976': 'Indian/Mayotte',
    };

    return timezones[code] || 'Europe/Paris';
};

const BoxReservationEventDateField = ({ label = 'Date événement' }) => (
    <FunctionField
        label={label}
        render={(record) => {
            if (!record?.event_date) return '—';

            return new Intl.DateTimeFormat('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                timeZone: getBoxReservationTimezone(record),
            }).format(new Date(record.event_date));
        }}
    />
);


const getBoxDateTimeParts = (value, timezone) => {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return null;

    const parts: Record<string, string> = {};

    new Intl.DateTimeFormat('en-CA', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
    }).formatToParts(date).forEach((part) => {
        if (part.type !== 'literal') {
            parts[part.type] = part.value;
        }
    });

    return parts;
};

const formatBoxEventDateForInput = (value, timezone) => {
    if (!value) return '';

    const p = getBoxDateTimeParts(value, timezone);

    if (!p) return '';

    return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
};

const parseBoxEventDateFromInput = (value, timezone) => {
    if (!value) return null;

    const m = String(value).match(
        /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/
    );

    if (!m) return value;

    const wanted = Date.UTC(
        Number(m[1]),
        Number(m[2]) - 1,
        Number(m[3]),
        Number(m[4]),
        Number(m[5]),
        0,
        0
    );

    let guess = wanted;

    for (let i = 0; i < 3; i += 1) {
        const p = getBoxDateTimeParts(new Date(guess), timezone);

        if (!p) break;

        const represented = Date.UTC(
            Number(p.year),
            Number(p.month) - 1,
            Number(p.day),
            Number(p.hour),
            Number(p.minute),
            Number(p.second || 0),
            0
        );

        guess += wanted - represented;
    }

    return new Date(guess).toISOString();
};

const BoxReservationEventDateInput = () => {
    const record = useRecordContext();
    const timezone = getBoxReservationTimezone(record);

    return (
        <TextInput
            source="event_date"
            type="datetime-local"
            label="Date et heure de l’événement"
            fullWidth
            format={(value) =>
                formatBoxEventDateForInput(value, timezone)
            }
            parse={(value) =>
                parseBoxEventDateFromInput(value, timezone)
            }
            helperText={`Heure locale : ${
                record?.delivery_zone_name || timezone
            }`}
        />
    );
};

const BoxContractActions = ({ compact = false }) => {
    const record = useRecordContext();
    const notify = useNotify();
    const refresh = useRefresh();
    const [loading, setLoading] = React.useState(false);
    const [emailDialogOpen, setEmailDialogOpen] = React.useState(false);
    const [emailRecipients, setEmailRecipients] = React.useState({
        team_email: '',
        client_email: '',
        bcc: [] as string[],
    });
    const [sendToTeam, setSendToTeam] = React.useState(true);
    const [sendToClient, setSendToClient] = React.useState(false);
    const [includeBcc, setIncludeBcc] = React.useState(true);

    if (!record) return null;

    const token = () => localStorage.getItem('auth') || '';

    const generate = async () => {
        if (loading || record.status !== 'paid') return;

        setLoading(true);

        try {
            const response = await fetch(
                `${apiURL}/boxreservation/${record.id}/contract`,
                {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        Authorization: `Bearer ${token()}`,
                    },
                }
            );

            if (!response.ok) throw new Error();

            notify('Contrat généré', 'info');
            refresh();
        } catch (error) {
            notify('Impossible de générer le contrat', 'warning');
        } finally {
            setLoading(false);
        }
    };

    const download = async () => {
        if (loading || !record.contract_number) return;

        setLoading(true);

        try {
            const response = await fetch(
                `${apiURL}/boxreservation/${record.id}/contract`,
                {
                    headers: {
                        Authorization: `Bearer ${token()}`,
                    },
                }
            );

            if (!response.ok) throw new Error();

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');

            link.href = url;
            link.download = `karaoke-kreyol-contrat-${record.contract_number}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            notify('Impossible de télécharger le contrat', 'warning');
        } finally {
            setLoading(false);
        }
    };

    const openSendDialog = async () => {
        if (loading || !record.contract_number) return;

        setLoading(true);

        try {
            const response = await fetch(
                `${apiURL}/boxreservation/${record.id}/contract/recipients`,
                {
                    headers: {
                        Accept: 'application/json',
                        Authorization: `Bearer ${token()}`,
                    },
                }
            );

            if (!response.ok) throw new Error();

            const recipients = await response.json();

            setEmailRecipients({
                team_email: recipients.team_email || '',
                client_email:
                    recipients.client_email || record.email || '',
                bcc: Array.isArray(recipients.bcc)
                    ? recipients.bcc
                    : [],
            });

            setSendToTeam(true);
            setSendToClient(false);
            setIncludeBcc(
                Array.isArray(recipients.bcc) &&
                    recipients.bcc.length > 0
            );
            setEmailDialogOpen(true);
        } catch (error) {
            notify(
                "Impossible de récupérer les destinataires",
                'warning'
            );
        } finally {
            setLoading(false);
        }
    };

    const send = async () => {
        if (
            loading ||
            !record.contract_number ||
            (
                !sendToTeam &&
                !sendToClient &&
                !(
                    includeBcc &&
                    emailRecipients.bcc.length > 0
                )
            )
        )
            return;

        setLoading(true);

        try {
            const response = await fetch(
                `${apiURL}/boxreservation/${record.id}/contract/email`,
                {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token()}`,
                    },
                    body: JSON.stringify({
                        send_to_team: sendToTeam,
                        send_to_client: sendToClient,
                        include_bcc:
                            includeBcc &&
                            emailRecipients.bcc.length > 0,
                    }),
                }
            );

            if (!response.ok) throw new Error();

            setEmailDialogOpen(false);
            notify('Contrat envoyé par email', 'info');
            refresh();
        } catch (error) {
            notify(
                "Impossible d'envoyer le contrat",
                'warning'
            );
        } finally {
            setLoading(false);
        }
    };

    if (compact) {
        return record.contract_number ? (
            <MuiButton
                size="small"
                color="primary"
                disabled={loading}
                onClick={download}
            >
                Télécharger
            </MuiButton>
        ) : (
            <span>—</span>
        );
    }

    if (!record.contract_number) {
        return (
            <MuiButton
                variant="contained"
                color="primary"
                disabled={record.status !== 'paid' || loading}
                onClick={generate}
                style={{
                    width: '100%',
                    maxWidth: 420,
                    whiteSpace: 'normal',
                    textAlign: 'center',
                }}
            >
                {loading ? 'Génération...' : 'GÉNÉRER LE CONTRAT'}
            </MuiButton>
        );
    }

    return (
        <div
            style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 8,
                width: '100%',
                maxWidth: 420,
            }}
        >
            <MuiButton
                variant="contained"
                color="primary"
                disabled={loading}
                onClick={download}
                style={{ flex: '1 1 190px' }}
            >
                TÉLÉCHARGER LE CONTRAT
            </MuiButton>

            <MuiButton
                variant="outlined"
                color="primary"
                disabled={loading}
                onClick={openSendDialog}
                style={{ flex: '1 1 190px' }}
            >
                ENVOYER LE CONTRAT PAR EMAIL
            </MuiButton>

            <Dialog
                open={emailDialogOpen}
                onClose={() => setEmailDialogOpen(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>
                    Envoyer le contrat par email
                </DialogTitle>

                <DialogContent>
                    <DialogContentText>
                        Choisis les destinataires du contrat.
                    </DialogContentText>

                    <div style={{ marginTop: 12 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={sendToTeam}
                                    onChange={(event) =>
                                        setSendToTeam(
                                            event.target.checked
                                        )
                                    }
                                    color="primary"
                                />
                            }
                            label={`Moi / équipe${
                                emailRecipients.team_email
                                    ? ` — ${emailRecipients.team_email}`
                                    : ' — aucun email configuré'
                            }`}
                        />

                        <br />

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={sendToClient}
                                    onChange={(event) =>
                                        setSendToClient(
                                            event.target.checked
                                        )
                                    }
                                    color="primary"
                                />
                            }
                            label={`Client — ${
                                emailRecipients.client_email ||
                                'aucun email'
                            }`}
                        />

                        {emailRecipients.bcc.length > 0 && (
                            <>
                                <br />

                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={includeBcc}
                                            onChange={(event) =>
                                                setIncludeBcc(
                                                    event.target.checked
                                                )
                                            }
                                            color="primary"
                                        />
                                    }
                                    label={`CCI de la région — ${emailRecipients.bcc.join(
                                        ', '
                                    )}`}
                                />
                            </>
                        )}
                    </div>
                </DialogContent>

                <DialogActions>
                    <MuiButton
                        onClick={() =>
                            setEmailDialogOpen(false)
                        }
                        disabled={loading}
                    >
                        Annuler
                    </MuiButton>

                    <MuiButton
                        color="primary"
                        variant="contained"
                        onClick={send}
                        disabled={
                            loading ||
                            (
                                !sendToTeam &&
                                !sendToClient &&
                                !(
                                    includeBcc &&
                                    emailRecipients.bcc.length > 0
                                )
                            )
                        }
                    >
                        {loading
                            ? 'Envoi...'
                            : 'Envoyer le contrat'}
                    </MuiButton>
                </DialogActions>
            </Dialog>
        </div>
    );
};

const BoxPaymentButton = () => {
    const record = useRecordContext();
    const notify = useNotify();
    const refresh = useRefresh();
    const [loading, setLoading] = React.useState(false);
    const [confirmOpen, setConfirmOpen] = React.useState(false);

    if (!record) return null;

    const sendContract = () => {
        if (loading) return;
        setConfirmOpen(true);
    };

    const confirmSendContract = async () => {
        if (loading) return;

        setConfirmOpen(false);
        setLoading(true);

        try {
            const token = localStorage.getItem('auth') || '';

            const response = await fetch(
                `${apiURL}/boxreservation/${record.id}/contract-acceptance`,
                {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            const body = await response.json().catch(() => ({}));

            if (!response.ok) {
                throw new Error(
                    body.message ||
                    `Erreur HTTP ${response.status}`
                );
            }

            notify(
                'Contrat envoyé au client pour acceptation.',
                'success'
            );

            refresh();
        } catch (error) {
            console.error(error);

            const message =
                error instanceof Error
                    ? error.message
                    : 'erreur inconnue';

            notify(
                `Impossible d’envoyer le contrat : ${message}`,
                'warning'
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <MuiButton
                variant="contained"
                color="primary"
                onClick={sendContract}
                disabled={
                    loading ||
                    record.contract_accepted === true ||
                    record.status === 'payment_sent' ||
                    record.status === 'paid' ||
                    record.status === 'cancelled'
                }
                style={{
                    width: '100%',
                    maxWidth: 420,
                    whiteSpace: 'normal',
                    textAlign: 'center',
                }}
            >
                {loading
                    ? 'Envoi du contrat...'
                    : record.contract_number
                    ? 'Renvoyer le contrat à accepter'
                    : 'Disponible — envoyer le contrat'}
            </MuiButton>

            <Dialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
            >
                <DialogTitle>
                    Confirmer l’envoi du contrat
                </DialogTitle>

                <DialogContent>
                    <DialogContentText>
                        Envoyer le contrat et les conditions générales de
                        location à{' '}
                        <strong>
                            {`${record.first_name || ''} ${record.last_name || ''}`.trim() || 'ce client'}
                        </strong>
                        {' '}pour acceptation avant paiement ?
                    </DialogContentText>
                </DialogContent>

                <DialogActions>
                    <MuiButton
                        onClick={() => setConfirmOpen(false)}
                    >
                        Annuler
                    </MuiButton>

                    <MuiButton
                        color="primary"
                        onClick={confirmSendContract}
                    >
                        Envoyer
                    </MuiButton>
                </DialogActions>
            </Dialog>
        </>
    );
};

const BoxPaymentReminderButton = ({ record }) => {
    const notify = useNotify();
    const refresh = useRefresh();
    const [loading, setLoading] = React.useState(false);
    const [confirmOpen, setConfirmOpen] = React.useState(false);

    if (!record || record.status !== 'payment_sent')
        return null;

    const sendReminder = async () => {
        if (loading) return;

        setConfirmOpen(false);
        setLoading(true);

        try {
            const token = localStorage.getItem('auth') || '';

            const response = await fetch(
                `${apiURL}/boxreservation/${record.id}/payment-reminder`,
                {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            const body = await response.json().catch(() => ({}));

            if (!response.ok) {
                throw new Error(
                    body.message ||
                    `Erreur HTTP ${response.status}`
                );
            }

            notify(
                'Email de relance envoyé.',
                'success'
            );

            refresh();
        } catch (error) {
            console.error(error);

            const message =
                error instanceof Error
                    ? error.message
                    : 'erreur inconnue';

            notify(
                `Impossible d’envoyer la relance : ${message}`,
                'warning'
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Button
                label={loading ? 'Envoi...' : 'Relancer'}
                onClick={() => setConfirmOpen(true)}
                disabled={loading}
            />

            <Dialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
            >
                <DialogTitle>
                    Confirmer la relance
                </DialogTitle>

                <DialogContent>
                    <DialogContentText>
                        Envoyer un email de relance de paiement à{' '}
                        <strong>
                            {`${record.first_name || ''} ${record.last_name || ''}`.trim() || 'ce client'}
                        </strong>
                        {' '}?
                    </DialogContentText>
                </DialogContent>

                <DialogActions>
                    <MuiButton
                        onClick={() => setConfirmOpen(false)}
                    >
                        Annuler
                    </MuiButton>

                    <MuiButton
                        color="primary"
                        onClick={sendReminder}
                    >
                        Relancer
                    </MuiButton>
                </DialogActions>
            </Dialog>
        </>
    );
};

const filters = [
    <TextInput source="all" label="Recherche" alwaysOn />,
    <SelectInput source="status" label="Statut" choices={STATUS_CHOICES} />,
];

export const BoxReservationList = (props) => (
    <List
        {...props}
        title={
            <ResourceListTitle
                resource="boxreservation"
                label="Réservations Box"
            />
        }
        filters={filters}
        bulkActionButtons={false}
        sort={{ field: 'event_date', order: 'DESC' }}
        perPage={50}
        pagination={<AdminPagination />}
        exporter={false}
    >
        <AdminSelectableDatagrid rowStyle={() => ({ whiteSpace: 'nowrap' })}>
            <BoxReservationEventDateField />
            <FunctionField
                label="Client"
                render={(record) =>
                    `${record.first_name || ''} ${record.last_name || ''}`.trim()
                }
            />
            <FunctionField
                label="Statut"
                render={(record) => {
                    const label =
                        STATUS_CHOICES.find(
                            (choice) => choice.id === record.status
                        )?.name || record.status;

                    const visual =
                        record.status === 'customer_accepted'
                            ? { type: 'circle', color: '#2e7d32' }
                            : record.status === 'customer_declined'
                            ? { type: 'circle', color: '#d32f2f' }
                            : record.status === 'payment_sent'
                            ? { type: 'triangle', color: '#f9a825' }
                            : null;

                    return (
                        <span
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 7,
                            }}
                        >
                            {visual?.type === 'circle' && (
                                <span
                                    style={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: '50%',
                                        backgroundColor: visual.color,
                                        display: 'inline-block',
                                        flexShrink: 0,
                                    }}
                                />
                            )}

                            {visual?.type === 'triangle' && (
                                <span
                                    style={{
                                        width: 0,
                                        height: 0,
                                        borderLeft: '6px solid transparent',
                                        borderRight: '6px solid transparent',
                                        borderBottom: `11px solid ${visual.color}`,
                                        display: 'inline-block',
                                        flexShrink: 0,
                                    }}
                                />
                            )}

                            {label}
                        </span>
                    );
                }}
            />
            <TextField source="phone" label="Téléphone" />
            <TextField source="city" label="Ville" />
            <TextField source="offer_name" label="Offre" />
            <NumberField
                source="total_price"
                label="Total (€)"
                options={{
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                }}
            />
            <DateField
                source="createdAt"
                label="Date de demande"
                showTime
            />
            <FunctionField
                label="Relance"
                render={(record) => (
                    <BoxPaymentReminderButton record={record} />
                )}
            />

            <FunctionField
                label="Facture"
                render={(record) => {
                    const invoiceUrl =
                        record.invoice_pdf_url ||
                        record.invoice_hosted_url;

                    return invoiceUrl ? (
                        <MuiButton
                            component="a"
                            href={invoiceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="small"
                            color="primary"
                        >
                            Télécharger
                        </MuiButton>
                    ) : '—';
                }}
            />
            <FunctionField
                label="Contrat"
                render={() => <BoxContractActions compact />}
            />
            <EditButton label="Ouvrir" />
            <HumanDeleteButton
                fields={['first_name', 'last_name']}
                separator=" "
                fallback="cette réservation"
                label="Supprimer"
            />
        </AdminSelectableDatagrid>
    </List>
);

const BoxReservationCancelButton = () => {
    const record = useRecordContext();
    const notify = useNotify();
    const refresh = useRefresh();

    const [open, setOpen] =
        React.useState(false);

    const [loading, setLoading] =
        React.useState(false);

    if (
        !record ||
        record.status === 'cancelled'
    ) {
        return null;
    }

    const confirmCancellation = async () => {
        if (loading) return;

        setLoading(true);

        try {
            const token =
                localStorage.getItem('auth') || '';

            const response = await fetch(
                `${apiURL}/boxreservation/${record.id}/cancel`,
                {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type':
                            'application/json',
                        Authorization:
                            `Bearer ${token}`,
                    },
                }
            );

            if (!response.ok) {
                throw new Error(
                    `HTTP ${response.status}`
                );
            }

            const result =
                await response.json();

            setOpen(false);

            if (
                result.client_email_sent &&
                result.internal_email_sent
            ) {
                notify(
                    'Réservation annulée. Client et équipe régionale informés.',
                    { type: 'success' }
                );
            } else if (
                result.client_email_sent
            ) {
                notify(
                    "Réservation annulée. Email client envoyé, mais l'email équipe/région doit être vérifié.",
                    { type: 'warning' }
                );
            } else {
                notify(
                    "Réservation annulée, mais l'envoi des emails doit être vérifié.",
                    { type: 'warning' }
                );
            }

            refresh();
        } catch (error) {
            console.error(error);

            notify(
                "L'annulation n'a pas pu être effectuée.",
                { type: 'error' }
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <MuiButton
                color="secondary"
                onClick={() => setOpen(true)}
                disabled={loading}
                style={{
                    marginLeft: 12,
                }}
            >
                ANNULER LA RÉSERVATION
            </MuiButton>

            <Dialog
                open={open}
                onClose={() => {
                    if (!loading) {
                        setOpen(false);
                    }
                }}
            >
                <DialogTitle>
                    Annuler la réservation ?
                </DialogTitle>

                <DialogContent>
                    <DialogContentText>
                        Cette action annulera la réservation
                        payée et neutralisera immédiatement
                        les liens DÉPOSE LIVRAISON et
                        RÉCUPÉRATION LIVRAISON.
                    </DialogContentText>

                    <DialogContentText
                        style={{
                            marginTop: 16,
                            fontWeight: 700,
                        }}
                    >
                        Le client ainsi que l'équipe de la
                        région seront informés par email.
                    </DialogContentText>

                    <DialogContentText
                        style={{
                            marginTop: 16,
                        }}
                    >
                        Aucun remboursement Stripe ne sera
                        effectué automatiquement.
                    </DialogContentText>
                </DialogContent>

                <DialogActions>
                    <MuiButton
                        onClick={() =>
                            setOpen(false)
                        }
                        disabled={loading}
                    >
                        RETOUR
                    </MuiButton>

                    <MuiButton
                        color="secondary"
                        onClick={
                            confirmCancellation
                        }
                        disabled={loading}
                    >
                        {loading
                            ? 'ANNULATION...'
                            : 'CONFIRMER L’ANNULATION'}
                    </MuiButton>
                </DialogActions>
            </Dialog>
        </>
    );
};

const BoxReservationDeleteButton = () => {
    const record = useRecordContext();

    return (
        <HumanDeleteButton
            record={record}
            fields={['first_name', 'last_name']}
            separator=" "
            fallback="cette réservation"
            label="Supprimer"
        />
    );
};

const BoxReservationToolbar = (props) => (
    <Toolbar {...props}>
        <SaveButton />
        <BoxReservationCancelButton />
        <BoxReservationDeleteButton />
    </Toolbar>
);

export const BoxReservationEdit = (props) => (
    <Edit
        {...props}
        mutationMode="optimistic"
        title={<HumanRecordTitle fields={["first_name", "last_name"]} fallback="Réservation Box" />}
    >
        <TabbedForm
            redirect="list"
            toolbar={<BoxReservationToolbar />}
            tabs={
                <TabbedFormTabs
                    variant="scrollable"
                    scrollButtons="auto"
                />
            }
        >
            <FormTab label="Résumé">
            <BoxReservationEventDateInput />
            <TextField source="event_name" label="Nom de l’événement" />
            <TextField source="first_name" label="Prénom" />
            <TextField source="last_name" label="Nom" />
            <FunctionField
                label="Téléphone"
                render={(record) => {
                    const phone = String(record?.phone || '').trim();

                    if (!phone) return '—';

                    const telPhone =
                        phone.replace(/[^+0-9]/g, '');

                    let whatsappPhone =
                        phone.replace(/\D/g, '');

                    if (whatsappPhone.startsWith('0')) {
                        whatsappPhone =
                            `33${whatsappPhone.slice(1)}`;
                    }

                    const copyPhone = async () => {
                        try {
                            await navigator.clipboard.writeText(phone);
                        } catch (error) {
                            console.error(error);
                        }
                    };

                    return (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                flexWrap: 'wrap',
                                whiteSpace: 'normal',
                            }}
                        >
                            <span
                                style={{
                                    display: 'block',
                                    width: '100%',
                                    marginBottom: 8,
                                }}
                            >
                                {phone}
                            </span>

                            <MuiButton
                                size="small"
                                variant="outlined"
                                component="a"
                                href={`tel:${telPhone}`}
                            >
                                APPELER
                            </MuiButton>

                            <MuiButton
                                size="small"
                                variant="outlined"
                                component="a"
                                href={`sms:${telPhone}`}
                            >
                                SMS
                            </MuiButton>

                            <MuiButton
                                size="small"
                                variant="outlined"
                                component="a"
                                href={`https://wa.me/${whatsappPhone}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                WHATSAPP
                            </MuiButton>

                            <MuiButton
                                size="small"
                                variant="outlined"
                                onClick={copyPhone}
                            >
                                COPIER
                            </MuiButton>
                        </div>
                    );
                }}
            />
            <TextField source="email" label="Email" />
            <TextField source="delivery_zone_name" label="Zone" />

            <TextField
                source="renter_address"
                label="Adresse du locataire"
            />
            <TextField
                source="renter_postal_code"
                label="Code postal du locataire"
            />
            <TextField
                source="renter_city"
                label="Ville / Commune du locataire"
            />

            <TextField
                source="address"
                label="Adresse de l’événement"
            />
            <TextField
                source="postal_code"
                label="Code postal de l’événement"
            />
            <TextField
                source="city"
                label="Ville / Commune de l’événement"
            />

            <FunctionField
                label="Même adresse"
                render={(record) =>
                    record.event_same_as_address
                        ? 'Oui'
                        : 'Non'
                }
            />

            <TextField source="comment" label="Commentaire client" />
            <FunctionField
                label="Vos envies Karaoké"
                render={(record) =>
                    [
                        record.karaoke_wish_1,
                        record.karaoke_wish_2,
                        record.karaoke_wish_3,
                    ]
                        .filter(Boolean)
                        .join(' • ')
                }
            />
            <TextField source="promo_code" label="Code promo" />
            <TextField source="promo_discount_percent" label="Remise (%)" />
            <TextField source="promo_discount_amount" label="Montant remise (€)" />
            <TextField source="offer_name" label="Offre choisie" />
            <NumberField source="offer_price" label="Prix offre (€)" />
            <NumberField
                source="deposit_amount"
                label="Dépôt de garantie (chèque) (€)"
                options={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }}
            />
            <FunctionField
                label="Prestations incluses"
                render={(record) =>
                    Array.isArray(record.offer_features)
                        ? record.offer_features.join(' • ')
                        : ''
                }
            />
            <FunctionField
                label="Options"
                render={(record) =>
                    Array.isArray(record.selected_options)
                        ? record.selected_options
                            .map((option) => `${option.name} (${Number(option.price).toFixed(2)} €)`)
                            .join(' • ')
                        : ''
                }
            />
            <NumberField
                source="total_price"
                label="Total (€)"
                options={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }}
            />

            <SelectInput
                source="status"
                label="Statut"
                choices={MANUAL_STATUS_CHOICES}
            />
            <FrenchDateTimeInput
                source="proposed_date"
                label="Nouvelle date proposée"
            />
            <FormDataConsumer>
                {({ formData }) => (
                    <BoxProposeDateButton
                        proposedDate={formData.proposed_date}
                    />
                )}
            </FormDataConsumer>
            <TextInput
                source="admin_comment"
                label="Commentaire admin"
                multiline
                rows={4}
                fullWidth
            />
            </FormTab>
            <FormTab label="Contrat">
            <FunctionField
                label="Contrat"
                render={() => <BoxContractActions />}
            />

            <FunctionField
                label="Preuve d’acceptation"
                render={(record) => {
                    const formatDateTime = (value) => {
                        if (!value) return '—';

                        const date = new Date(value);

                        if (Number.isNaN(date.getTime())) return '—';

                        return date.toLocaleString('fr-FR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                        });
                    };

                    return (
                        <div
                            style={{
                                width: '100%',
                                maxWidth: 520,
                                padding: 14,
                                border: '1px solid #ddd',
                                borderRadius: 6,
                                background: '#fafafa',
                                lineHeight: 1.7,
                            }}
                        >
                            <div>
                                <strong>Numéro du contrat :</strong>{' '}
                                {record.contract_number || '—'}
                            </div>

                            <div>
                                <strong>Envoyé pour acceptation :</strong>{' '}
                                {formatDateTime(
                                    record.contract_acceptance_sent_at
                                )}
                            </div>

                            <div>
                                <strong>Statut :</strong>{' '}
                                {record.contract_accepted
                                    ? 'Accepté'
                                    : 'En attente'}
                            </div>

                            <div>
                                <strong>Accepté le :</strong>{' '}
                                {formatDateTime(record.contract_accepted_at)}
                            </div>

                            <div>
                                <strong>Numéro du contrat accepté :</strong>{' '}
                                {record.contract_accepted_number || '—'}
                            </div>
                        </div>
                    );
                }}
            />

            <BoxPaymentButton />
            </FormTab>
            <FormTab label="Paiement">
            <FunctionField
                label="Paiement Stripe"
                render={(record) => {
                    if (record.status === 'paid')
                        return 'Paiement reçu';

                    return record.payment_url ? (
                        <MuiButton
                            component="a"
                            href={record.payment_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="small"
                            color="primary"
                        >
                            Ouvrir le paiement
                        </MuiButton>
                    ) : '—';
                }}
            />

            <FunctionField
                label="Relance"
                render={(record) => (
                    <BoxPaymentReminderButton record={record} />
                )}
            />

            <FunctionField
                label="Facture"
                render={(record) => {
                    const invoiceUrl =
                        record.invoice_pdf_url ||
                        record.invoice_hosted_url;

                    return invoiceUrl ? (
                        <MuiButton
                            component="a"
                            href={invoiceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="small"
                            color="primary"
                        >
                            Télécharger la facture
                        </MuiButton>
                    ) : '—';
                }}
            />

            <FunctionField
                label="Créer l’événement"
                render={(record) => (
                    <MuiButton
                        variant="contained"
                        color="primary"
                        disabled={record.status !== 'paid'}
                        onClick={() => {
                            if (record.status !== 'paid') return;

                            const frontUrl =
                                window.location.hostname === 'admin-test.karaoke-kreyol.com'
                                    ? 'https://test.karaoke-kreyol.com'
                                    : 'https://karaoke-kreyol.com';

                            window.location.href =
                                `${frontUrl}/utilisateur/evenements?create=1`;
                        }}
                        style={{
                            width: '100%',
                            maxWidth: 420,
                            whiteSpace: 'normal',
                            textAlign: 'center',
                        }}
                    >
                        Créer l’événement dans mon espace compte
                    </MuiButton>
                )}
            />

            </FormTab>

            <FormTab label="Matériel">
                <EquipmentReturnMirror />

                <div style={{ fontWeight: 700, marginBottom: 12 }}>
                    ÉTAT DU MATÉRIEL — DÉPART
                </div>

                <ArrayInput
                    source="equipment_departure"
                    label=""
                    defaultValue={[
                        { name: '1 Enceinte de marque JBL PartyBox', good_condition: true, anomaly: false },
                        { name: '2 Micros sans fil JBL', good_condition: true, anomaly: false },
                        { name: 'Récepteur HF JBL', good_condition: true, anomaly: false },
                        { name: 'Ordinateur portable HP', good_condition: true, anomaly: false },
                        { name: 'Câblage complet', good_condition: true, anomaly: false },
                        { name: 'Boîte de transport', good_condition: true, anomaly: false },
                        { name: 'Housse de transport', good_condition: true, anomaly: false },
                    ]}
                >
                    <SimpleFormIterator>
                        <FormDataConsumer>
                            {({ getSource }) => getSource ? (
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        flexWrap: 'nowrap',
                                        gap: 20,
                                        width: '100%',
                                    }}
                                >
                                    <TextInput
                                        source={getSource('name')}
                                        label="Matériel"
                                        style={{ width: 280 }}
                                    />
                                    <EquipmentCheckboxInput
                                        source={getSource('good_condition')}
                                        oppositeSource={getSource('anomaly')}
                                        label="Bon état"
                                    />
                                    <EquipmentCheckboxInput
                                        source={getSource('anomaly')}
                                        oppositeSource={getSource('good_condition')}
                                        label="Anomalie"
                                    />
                                </div>
                            ) : null}
                        </FormDataConsumer>
                    </SimpleFormIterator>
                </ArrayInput>

                <FrenchDateTimeInput
                    source="equipment_departure_at"
                    label="Date et heure du départ"
                />

                <TextInput
                    source="equipment_departure_observation"
                    label="Observations au départ"
                    multiline
                    rows={4}
                    fullWidth
                />

                <BoxCustomerHandover />

                <div style={{ fontWeight: 700, marginTop: 28, marginBottom: 12 }}>
                    ÉTAT DU MATÉRIEL — RETOUR
                </div>

                <ArrayInput
                    source="equipment_return"
                    label=""
                    defaultValue={[
                        { name: '1 Enceinte de marque JBL PartyBox', good_condition: false, anomaly: false },
                        { name: '2 Micros sans fil JBL', good_condition: false, anomaly: false },
                        { name: 'Récepteur HF JBL', good_condition: false, anomaly: false },
                        { name: 'Ordinateur portable HP', good_condition: false, anomaly: false },
                        { name: 'Câblage complet', good_condition: false, anomaly: false },
                        { name: 'Boîte de transport', good_condition: false, anomaly: false },
                        { name: 'Housse de transport', good_condition: false, anomaly: false },
                    ]}
                >
                    <SimpleFormIterator disableAdd disableRemove disableReordering>
                        <FormDataConsumer>
                            {({ getSource }) => getSource ? (
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        flexWrap: 'nowrap',
                                        gap: 20,
                                        width: '100%',
                                    }}
                                >
                                    <TextInput
                                        source={getSource('name')}
                                        label="Matériel"
                                        style={{ width: 280 }}
                                        disabled
                                    />
                                    <EquipmentCheckboxInput
                                        source={getSource('good_condition')}
                                        oppositeSource={getSource('anomaly')}
                                        label="Bon état"
                                    />
                                    <EquipmentCheckboxInput
                                        source={getSource('anomaly')}
                                        oppositeSource={getSource('good_condition')}
                                        label="Anomalie"
                                    />
                                </div>
                            ) : null}
                        </FormDataConsumer>
                    </SimpleFormIterator>
                </ArrayInput>

                <FrenchDateTimeInput
                    source="equipment_return_at"
                    label="Date et heure du retour"
                />

                <TextInput
                    source="equipment_return_observation"
                    label="Observations au retour"
                    multiline
                    rows={4}
                    fullWidth
                />
            </FormTab>

            <FormTab label="Caution">
                <NumberField
                    source="deposit_amount"
                    label="Montant de la caution (€)"
                    options={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }}
                />

                <FunctionField
                    label="Mode de caution"
                    render={(record) =>
                        record.deposit_method === 'cheque' ||
                        !record.deposit_method
                            ? 'Chèque'
                            : record.deposit_method
                    }
                />

                <FunctionField
                    label="Caution reçue au départ"
                    render={(record) =>
                        record.deposit_received
                            ? 'Oui'
                            : 'Non'
                    }
                />

                <FunctionField
                    label="Reçue le"
                    render={(record) =>
                        record.deposit_received_at
                            ? new Date(
                                  record.deposit_received_at
                              ).toLocaleString('fr-FR')
                            : '—'
                    }
                />

                <SelectInput
                    source="deposit_status"
                    label="Statut de la caution"
                    choices={[
                        { id: '', name: 'À traiter' },
                        { id: 'returned', name: 'Restituée' },
                        { id: 'partial', name: 'Retenue partiellement' },
                        { id: 'retained', name: 'Retenue' },
                    ]}
                />

                <FrenchDateTimeInput
                    source="deposit_processed_at"
                    label="Date de traitement"
                />

                <NumberInput
                    source="deposit_retained_amount"
                    label="Montant retenu (€)"
                    min={0}
                />

                <TextInput
                    source="deposit_observation"
                    label="Observation caution"
                    multiline
                    rows={4}
                    fullWidth
                />
            </FormTab>

            <FormTab label="Remboursement">
                <FunctionField
                    label="Remboursement Stripe"
                    render={() => <BoxRefundPanel />}
                />
            </FormTab>
        </TabbedForm>
    </Edit>
);
